import {
  BlurMask,
  Canvas,
  Path,
  SweepGradient,
  Skia,
  vec,
} from '@shopify/react-native-skia';
import { forwardRef, useImperativeHandle, useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import Animated, {
  Easing,
  FadeIn,
  FadeOut,
  interpolate,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withRepeat,
  withTiming,
  cancelAnimation,
} from 'react-native-reanimated';

import { InteractiveForwardRefProps } from '@/components/interactive';

const DEFAULT_SIZE = 128;

type LoaderProps = {
  type:
    | 'basic'
    | 'with-gradient'
    | 'with-animation'
    | 'with-basic-animation'
    | 'with-progress';
  start?: number;
};

export const Loader = forwardRef<InteractiveForwardRefProps, LoaderProps>(
  ({ type, start: startProp }, ref) => {
    const size = DEFAULT_SIZE;
    const strokeWidth = 15;
    const radius = (size - strokeWidth) / 2;
    const canvasSize = size + 30;

    const circle = useMemo(() => {
      const skPath = Skia.Path.Make();
      skPath.addCircle(canvasSize / 2, canvasSize / 2, radius);
      return skPath;
    }, [canvasSize, radius]);

    const progress = useSharedValue(0);

    // Implement play/reset methods for the interactive controls
    useImperativeHandle(
      ref,
      () => ({
        play: () => {
          progress.value = 0;
          if (type === 'with-animation' || type === 'with-basic-animation') {
            progress.value = withRepeat(
              withTiming(1, { duration: 1000, easing: Easing.linear }),
              -1,
              false
            );
          }
        },
        reset: () => {
          cancelAnimation(progress);
          progress.value = 0;
        },
        pause: () => {
          cancelAnimation(progress);
        },
        setProgress: (value: number) => {
          progress.value = value;
        },
      }),
      [progress, type]
    );

    const rContainerStyle = useAnimatedStyle(() => {
      if (type !== 'with-animation' && type !== 'with-basic-animation') {
        return {};
      }
      return {
        transform: [{ rotate: `${2 * Math.PI * progress.value}rad` }],
      };
    }, [type, progress]);

    const startPath = useDerivedValue(() => {
      if (type === 'basic') {
        return 0;
      }
      if (type === 'with-animation') {
        return interpolate(progress.value, [0, 0.5, 1], [0.6, 0.3, 0.6]);
      }
      if (type === 'with-progress') {
        return startProp ?? 0.6;
      }
      // All other types have a static path
      return 0.6;
    }, [type, progress, startProp]);

    const Container =
      type === 'with-animation' || type === 'with-basic-animation'
        ? Animated.View
        : View;
    const containerProps =
      type === 'with-animation' || type === 'with-basic-animation'
        ? {
            entering: FadeIn.duration(1000),
            exiting: FadeOut.duration(1000),
            style: rContainerStyle,
          }
        : {};

    return (
      <View style={styles.container}>
        <Container {...containerProps}>
          <Canvas
            style={{
              width: canvasSize,
              height: canvasSize,
            }}
          >
            <Path
              path={circle}
              color={
                type === 'basic' || type === 'with-progress'
                  ? '#6370CA'
                  : undefined
              }
              style="stroke"
              strokeWidth={strokeWidth}
              start={startPath}
              end={1}
              strokeCap="round"
            >
              {type !== 'basic' && type !== 'with-progress' && (
                <>
                  <SweepGradient
                    c={vec(canvasSize / 2, canvasSize / 2)}
                    colors={['cyan', 'magenta', 'yellow', 'cyan']}
                  />
                  <BlurMask blur={5} style="solid" />
                </>
              )}
            </Path>
          </Canvas>
        </Container>
      </View>
    );
  }
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
